// Carrier detail header — FE-SCRM-030.
//
// Shows the carrier name, type + status badges, and the write-action
// buttons (Edit / Add contact). The buttons are gated on
// ``canManageCarriers`` from the caller; the header itself doesn't
// re-check permissions, so the gate lives in one place upstream.

import { Link } from "react-router-dom";

import { Badge } from "@/design-system";

import { CARRIER_STATUS_LABEL, CARRIER_TYPE_LABEL } from "./labels";
import type { CarrierDetail } from "./types";


interface Props {
  carrier: CarrierDetail;
  canManageCarriers: boolean;
  onEditCarrier: () => void;
  onAddContact: () => void;
  onDeleteCarrier: () => void;
}


export function CarrierDetailHeader({
  carrier,
  canManageCarriers,
  onEditCarrier,
  onAddContact,
  onDeleteCarrier,
}: Props) {
  return (
    <div
      data-testid="carrier-detail-header"
      className="flex flex-col gap-3"
    >
      <nav
        aria-label="Breadcrumb"
        className="text-xs text-text-tertiary"
        data-testid="carrier-detail-breadcrumb"
      >
        <Link
          to="/dashboard/carriers"
          className="hover:text-text-primary"
        >
          Carriers
        </Link>
        <span className="mx-1.5">/</span>
        <span className="text-text-secondary">{carrier.name}</span>
      </nav>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-lg font-semibold tracking-tight text-text-primary">
            {carrier.name}
          </h2>
          <Badge variant="neutral">
            {CARRIER_TYPE_LABEL[carrier.carrier_type] ?? carrier.carrier_type}
          </Badge>
          <Badge
            variant={carrier.status === "active" ? "category" : "neutral"}
          >
            {CARRIER_STATUS_LABEL[carrier.status] ?? carrier.status}
          </Badge>
        </div>
        {canManageCarriers && (
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              data-testid="carrier-detail-edit"
              onClick={onEditCarrier}
              className="rounded-md border border-border-subtle px-3 py-1.5 text-sm text-text-primary transition-fast ease-out hover:bg-recessed"
            >
              Edit carrier
            </button>
            <button
              type="button"
              data-testid="carrier-detail-delete"
              onClick={onDeleteCarrier}
              className="rounded-md border border-border-subtle px-3 py-1.5 text-sm text-ember-ink transition-fast ease-out hover:bg-recessed"
            >
              Delete
            </button>
            <button
              type="button"
              data-testid="carrier-detail-add-contact"
              onClick={onAddContact}
              className="inline-flex items-center gap-1 rounded-md bg-teal px-3 py-1.5 text-sm font-medium text-text-inverse transition-fast ease-out hover:opacity-90"
            >
              + Add contact
            </button>
          </div>
        )}
      </div>

      <CarrierMetadataPanel carrier={carrier} />
    </div>
  );
}


function CarrierMetadataPanel({ carrier }: { carrier: CarrierDetail }) {
  const items: Array<{ label: string; value: React.ReactNode }> = [];
  if (carrier.naic_code) {
    items.push({ label: "NAIC", value: carrier.naic_code });
  }
  if (carrier.website) {
    items.push({
      label: "Website",
      value: (
        <a
          href={carrier.website}
          target="_blank"
          rel="noopener noreferrer"
          className="text-teal underline-offset-2 hover:underline"
        >
          {carrier.website}
        </a>
      ),
    });
  }
  if (carrier.parent_carrier_id) {
    items.push({
      label: "Parent",
      value: (
        <Link
          to={`/dashboard/carriers/${encodeURIComponent(carrier.parent_carrier_id)}`}
          className="text-teal underline-offset-2 hover:underline"
        >
          View parent
        </Link>
      ),
    });
  }
  if (items.length === 0 && !carrier.notes) return null;
  return (
    <dl
      data-testid="carrier-detail-metadata"
      className="flex flex-col gap-2 rounded-md border border-border-subtle bg-recessed/40 p-3 text-sm"
    >
      {items.length > 0 && (
        <div className="flex flex-wrap gap-x-6 gap-y-1">
          {items.map((item) => (
            <div key={item.label} className="flex gap-1">
              <dt className="text-text-tertiary">{item.label}:</dt>
              <dd className="text-text-primary">{item.value}</dd>
            </div>
          ))}
        </div>
      )}
      {carrier.notes && (
        <div>
          <dt className="sr-only">Notes</dt>
          <dd
            className="whitespace-pre-line text-text-secondary"
            data-testid="carrier-detail-notes"
          >
            {carrier.notes}
          </dd>
        </div>
      )}
    </dl>
  );
}
